import {showCommunicationError, showNotFoundToast, showSuccess} from 'helpers/alerts';
import {t} from 'i18next';
import {action, makeObservable, observable} from 'mobx';
import {browserHistory, redirectTo} from 'routing/browserHistory';
import {AppRoutePaths} from 'routing/Routes';
import {ServiceActionDto, ServiceStatus, ServiceTaskCreateDto, ServiceTaskDto} from '../../../../../PRMGApiClient';
import {Api} from '../../../../../PRMGApiClient/ApiClient';

export class TaskState {
  @observable
  item: ServiceTaskDto = {
    id: undefined,
    name: undefined,
    startDateTime: undefined,
    endDateTime: undefined,
    status: undefined,
    assignee: undefined,
    description: undefined,
    attachments: [],
    comments: [],
  };

  @observable
  serviceAction: ServiceActionDto | undefined;

  @observable
  isLoading: boolean = false;

  @observable
  statusEdit: boolean = false;

  @observable
  status: ServiceStatus | undefined;

  constructor() {
    makeObservable(this);
  }

  @action
  loadTask(taskId: number | undefined) {
    if (!taskId) return;

    Api.ServiceAction.apiServiceActionGetTaskIdGet({
      id: taskId,
    })
      .then((res) => {
        if (res.content) {
          this.item = res.content;
          if (res.content.serviceActionId) {
            Api.ServiceAction.apiServiceActionGetIdGet({
              id: res.content.serviceActionId,
            }).then((res) => {
              if (res.content) {
                this.serviceAction = res.content;
              }
            });
          }
        } else {
          showNotFoundToast();
          setTimeout(() => redirectTo(AppRoutePaths.tasks.url), 1000);
        }
      })
      .catch(showCommunicationError);
  }

  @action
  addAttachments(files: File[]) {
    if (!this.item.id) return;

    this.isLoading = true;
    Api.ServiceAction.apiServiceActionAddTaskAttachmentsPost({
      serviceTaskId: this.item.id,
      request: files,
    })
      .then(() => {
        this.loadTask(this.item.id);
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  removeAttachment(fileId: number) {
    if (!this.item.id) return;

    this.isLoading = true;
    Api.ServiceAction.apiServiceActionDeleteTaskAttachmentDelete({
      serviceTaskId: this.item.id,
      fileId,
    })
      .then(() => this.loadTask(this.item.id))
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  addComment({comment}: {comment: string}) {
    if (!this.item.id) return;

    return Api.ServiceAction.apiServiceActionAddCommentTaskIdPostRaw({
      taskId: this.item.id,
      commentRequest: {value: comment},
    }).then(() => this.loadTask(this.item.id));
  }

  @action
  editComment({commentId, value}: {commentId: number; value: string}) {
    if (!this.item.id) return;

    Api.ServiceAction.apiServiceActionEditCommentCommentIdPut({
      commentId,
      commentRequest: {value},
    }).then(() => this.loadTask(this.item.id));
  }

  @action
  removeComment(commentId: number) {
    if (!this.item.id) return;

    Api.ServiceAction.apiServiceActionDeleteCommentCommentIdDelete({
      commentId,
    }).then(() => this.loadTask(this.item.id));
  }

  @action
  updateStatus() {
    if (!this.item.id) return;

    Api.ServiceAction.apiServiceActionUpdateStatusPut({
      status: this.status,
      taskId: this.item.id,
    }).then(() => this.loadTask(this.item.id));
  }

  @action
  setStatusEdit(statusEdit: boolean) {
    this.statusEdit = statusEdit;
  }

  @action
  setStatus(status: ServiceStatus) {
    this.status = status;
  }

  @action
  saveTask(payload: ServiceTaskCreateDto, serviceId?: number) {
    this.item = payload;
    payload.id ? this.updateTask(payload) : serviceId && this.addTask(payload, serviceId);
  }

  @action
  addTask(payload: ServiceTaskCreateDto, serviceId: number): void {
    this.isLoading = true;
    Api.ServiceAction.apiServiceActionAddTaskPost({
      serviceActionId: serviceId,
      serviceTaskCreateDto: payload,
    })
      .then(() => browserHistory.back())
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  updateTask(payload: ServiceTaskCreateDto): void {
    this.isLoading = true;
    Api.ServiceAction.apiServiceActionUpdateTaskPut({
      serviceTaskUpdateDto: {...payload},
    })
      .then(() => {
        showSuccess(t('Changes saved'));
        browserHistory.back();
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  deleteTask() {
    this.isLoading = true;
    Api.ServiceAction.apiServiceActionDeleteTaskDelete({
      id: this.item.id,
    })
      .then(() => {
        showSuccess(t('Task deleted'));
        redirectTo(AppRoutePaths.tasks.url);
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }
}
